import { useContext, useEffect } from 'react'

import { useApiGetStoreForecast } from 'core/api/trajectory-photo/useApiGetStoreForecast'
import { StoreTrajectoryContext } from '../state/store-trajectory-context'
import { REFERENCE_STORE_REQUIRED_ERROR_CODE } from 'core/api/trajectory-photo/storeForecastError'

const computeValue = (accountReferenceGmv: number | null, referenceGmv: number | null, targetGmv: number | null) => {
  if (accountReferenceGmv == null || referenceGmv == null || targetGmv == null) {
    return null
  }
  const accountPercent = (accountReferenceGmv * 100) / referenceGmv
  return (accountPercent / 100) * targetGmv
}

const useStoreTrajectoryEditValues = (referenceStoreId?: number) => {
  const {
    storeId,
    targetYear: forecastYear,
    setReferenceYear,
    setForecastInstoreGmv,
    setForecastInstoreMarginRate,
    setForecastInstoreSellingPrice,
    setForecastInstorePhysical,
    setForecastInstoreDigital,
    setForecastInstoreDecapro,
    setForecastInstoreCircular,
    setForecastInstoreNonCircular,
    setForecastInstoreRepair,
    setForecastInstoreSecondLife,
    setForecastInstoreBuyBack,
    setForecastInstoreRental,
    setForecastOutstoreGmv,
    setForecastOutstoreMarginRate,
    setForecastOutstoreSellingPrice
  } = useContext(StoreTrajectoryContext)

  const { data, isLoading, error } = useApiGetStoreForecast(
    storeId,
    forecastYear,
    !!storeId && !!forecastYear,
    referenceStoreId
  )
  const isReferenceStoreRequired = error?.response?.data?.error_code === REFERENCE_STORE_REQUIRED_ERROR_CODE

  const { instore, outstore, reference_year } = data || {}

  useEffect(() => {
    setReferenceYear(reference_year)
    setForecastInstoreGmv(instore?.target_gmv)
    setForecastInstoreMarginRate(instore?.target_margin_rate)
    setForecastInstoreSellingPrice(instore?.target_selling_price)

    const initialPhysicalGmv = computeValue(
      instore?.reference_physical_gmv,
      instore?.reference_gmv,
      instore?.target_gmv
    )
    setForecastInstorePhysical(initialPhysicalGmv)

    const initialDigitalGmv = computeValue(instore?.reference_digital_gmv, instore?.reference_gmv, instore?.target_gmv)
    setForecastInstoreDigital(initialDigitalGmv)

    const initialDecaproGmv = computeValue(instore?.reference_decapro_gmv, instore?.reference_gmv, instore?.target_gmv)
    setForecastInstoreDecapro(initialDecaproGmv)

    const initialCircular = computeValue(
      instore?.reference_eco_circular_gmv,
      instore?.reference_physical_gmv,
      initialPhysicalGmv
    )
    setForecastInstoreCircular(initialCircular)

    const initialNonCircular = computeValue(
      instore?.reference_eco_non_circular_gmv,
      instore?.reference_physical_gmv,
      initialPhysicalGmv
    )
    setForecastInstoreNonCircular(initialNonCircular)

    const initialRepair = computeValue(
      instore?.reference_eco_circular_repair_and_maintenance_gmv,
      instore?.reference_eco_circular_gmv,
      initialCircular
    )
    setForecastInstoreRepair(initialRepair)

    const initialSavedProducts = computeValue(
      instore?.reference_eco_circular_saved_products_gmv,
      instore?.reference_eco_circular_gmv,
      initialCircular
    )
    setForecastInstoreSecondLife(initialSavedProducts)

    const initialBuyBack = computeValue(
      instore?.reference_eco_circular_buy_back_gmv,
      instore?.reference_eco_circular_gmv,
      initialCircular
    )
    setForecastInstoreBuyBack(initialBuyBack)

    const initialRental = computeValue(
      instore?.reference_eco_circular_rental_gmv,
      instore?.reference_eco_circular_gmv,
      initialCircular
    )
    setForecastInstoreRental(initialRental)

    setForecastOutstoreGmv(outstore?.target_gmv)
    setForecastOutstoreMarginRate(outstore?.target_margin_rate)
    setForecastOutstoreSellingPrice(outstore?.target_selling_price)
  }, [instore, outstore, reference_year])

  return {
    isLoading,
    isReferenceStoreRequired,
    instore,
    outstore
  }
}

export default useStoreTrajectoryEditValues
